import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarreraDeErrores } from '../barrera-de-errores';
import { useIsMobile } from '@/hooks/use-media-query';
import { useAuth, useCurrentUser } from '@/app/auth-provider';
import { api, type ApiEnvelope } from '@/lib/api-client';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { MobileNav } from './mobile-nav';
import { MenuMovil } from './menu-movil';
import { CommandPalette } from '@/features/search/command-palette';
import { ConfigurarMfa } from '@/features/auth/configurar-mfa';
import { Tutorial } from '@/features/tutorial/tutorial';
import { AyudaFlotante } from '@/features/ayuda/ayuda-flotante';
import { marcarTutorialVisto, tutorialVisto } from '@/features/tutorial/estado';

const COLLAPSE_KEY = 'obralink:sidebar-collapsed';

export function AppLayout() {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === 'true');
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  const [tutorialAbierto, setTutorialAbierto] = useState(false);

  const user = useCurrentUser();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // ── Segundo factor obligatorio ─────────────────────────────────────────────
  //
  // La API le exige el segundo factor a un administrador y, hasta que lo tenga,
  // contesta 403 a todo lo que no sea /auth. Sin esta pantalla el administrador
  // entraba, veía el menú, y ninguna sección cargaba. Sólo se pregunta a los
  // administradores: el resto no tiene nada que configurar.
  const mfa = useQuery({
    queryKey: ['mfa-estado'],
    queryFn: () => api.get<ApiEnvelope<{ activo: boolean; obligatorio: boolean }>>('/auth/mfa'),
    enabled: user.roleCode === 'ADMIN',
    staleTime: Infinity,
    retry: false,
  });
  const pendienteDeMfa =
    user.roleCode === 'ADMIN' && !!mfa.data && mfa.data.data.obligatorio && !mfa.data.data.activo;
  const comprobandoMfa = user.roleCode === 'ADMIN' && mfa.isPending;

  // ── Tutorial de bienvenida: la primera vez que entra cada persona ───────────
  //
  // Se marca como visto AL EMPEZAR, no al terminar: quien cierra la pestaña a
  // mitad no tiene por qué volver a verlo entero en cada visita. Espera a que
  // pase la pantalla del segundo factor y a que el menú esté pintado.
  useEffect(() => {
    if (pendienteDeMfa || comprobandoMfa || tutorialVisto(user.id)) return;
    const t = window.setTimeout(() => {
      marcarTutorialVisto(user.id);
      setTutorialAbierto(true);
    }, 900);
    return () => window.clearTimeout(t);
  }, [user.id, pendienteDeMfa, comprobandoMfa]);

  // La página de Ayuda tiene un botón para repetir el tutorial.
  useEffect(() => {
    const abrir = () => setTutorialAbierto(true);
    window.addEventListener('obralink:tutorial', abrir);
    return () => window.removeEventListener('obralink:tutorial', abrir);
  }, []);

  // Al pasar a escritorio, el cajón del móvil no puede quedarse abierto.
  useEffect(() => {
    if (!isMobile) setMenuMovilAbierto(false);
  }, [isMobile]);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  // ⌘K / Ctrl+K abre el buscador desde cualquier punto de la aplicación
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const { pathname } = useLocation();

  return (
    <div className="flex h-full overflow-hidden bg-bg">
      {!isMobile && (
        <Sidebar user={user} collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          user={user}
          onOpenMobileNav={() => setMenuMovilAbierto(true)}
          onOpenSearch={() => setSearchOpen(true)}
          onStartTutorial={() => setTutorialAbierto(true)}
        />

        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* La barrera va AQUÍ DENTRO: si una pantalla revienta, el menú y la
              barra superior siguen puestos y se puede uno ir a otro sitio. */}
          <BarreraDeErrores clave={pathname}>
            {pendienteDeMfa ? (
              <ConfigurarMfa
                onTerminado={() => void mfa.refetch()}
                onSalir={() => void logout().then(() => navigate('/acceso', { replace: true }))}
              />
            ) : (
              <Outlet />
            )}
          </BarreraDeErrores>
        </main>

        {isMobile && <MobileNav onMas={() => setMenuMovilAbierto(true)} />}
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
      {!pendienteDeMfa && <AyudaFlotante user={user} esMovil={isMobile} />}
      <MenuMovil user={user} abierto={menuMovilAbierto} onCambio={setMenuMovilAbierto} />
      {tutorialAbierto && (
        <Tutorial esMovil={isMobile} onCerrar={() => setTutorialAbierto(false)} />
      )}
    </div>
  );
}
